import { Component, EventEmitter, HostListener, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { NbDialogRef, NbDialogService, NbMenuItem, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Observable, Subject, Subscription, forkJoin, from, fromEvent, of, switchMap, takeUntil } from 'rxjs';
import { catchError, concatMap, map, reduce, takeWhile, tap } from 'rxjs/operators';

import {
  CourseJobLane,
  CourseJobState,
  CourseJobStatus,
  LmsPlacement,
  LmsRegistration,
  LmsRegistryService,
  MoodleCourse,
  MoodleCourseListResponse,
} from '../../services/lms-registry.service';
import { DataSource } from '../../services/data-sources.service';
import {
  DOZENT_COMPATIBLE_TUTOR_TEMPLATES,
  DozentBot,
  Tutor,
  TutorService,
  TutorTemplate,
} from '../../services/tutor.service';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import {
  OrbiDistributeDialogComponent,
  OrbiDistributeMode,
} from '../dialogs/orbi-distribute-dialog/orbi-distribute-dialog.component';
import { DialogSuppressionService } from '../../services/dialog-suppression.service';
import { CourseStructureDialogComponent } from '../dialogs/course-structure-dialog/course-structure-dialog.component';
import { ParsedCourseStructureDialogComponent } from '../../pages/moodle-dashboard/detail-dialogs/course-structure-dialog.component';
import { MoodleService } from '../../services/moodle.service';
import { PlacementEditDialogComponent } from '../dialogs/placement-edit-dialog/placement-edit-dialog.component';
import { PlacementCreateDialogComponent } from '../dialogs/placement-create-dialog/placement-create-dialog.component';
import { UsageService } from '../../services/usage.service';
import { CourseSyncJobTrackerService } from '../../services/course-sync-job-tracker.service';
import { OrganisationService } from '../../services/organisation.service';
import { DozentService } from '../../services/dozent.service';
import { ChatPreviewService } from '../../services/chat-preview.service';

interface CourseRow {
  course: MoodleCourse;
  /** existing placement on this Moodle course, if any */
  placement?: LmsPlacement;
  /** edit buffer for the inline-editable cells. Populated from the
   *  placement's bot + that bot's KB list / dozent attachment when
   *  the row first renders. Reset to those values after a successful
   *  save so the dirty flag flips back to false. */
  edit: {
    botId: string | null;
    kbIds: string[];
    dozentEnabled: boolean;
    dozentBotId: string | null;
    courseSyncEnabled: boolean;
    structureParseEnabled: boolean;
  };
  initial: {
    botId: string | null;
    kbIds: string[];
    dozentEnabled: boolean;
    dozentBotId: string | null;
    courseSyncEnabled: boolean;
    structureParseEnabled: boolean;
  };
  saving: boolean;
  refreshingCourse: boolean;
  /** Independent flag for the inline course-sync toggle (separate
   *  from the bundled `saving` flag for Save-button calls — the toggle
   *  is committed immediately on click rather than via Save). */
  savingSync: boolean;
  /** Independent flag for the inline structure-parse (advanced sync)
   *  toggle — committed immediately on click like the sync toggle. */
  savingStructureParse: boolean;
}

interface BulkSyncResult {
  courseId: number;
  ok: boolean;
  placement?: LmsPlacement;
  error?: unknown;
}

interface BulkPlaceResult {
  courseId: number;
  ok: boolean;
  placement?: LmsPlacement;
  error?: unknown;
}

interface BulkRemoveResult {
  courseId: number;
  ok: boolean;
  error?: unknown;
}

interface BulkUnsyncResult {
  courseId: number;
  ok: boolean;
  error?: unknown;
}

interface BulkProgress {
  done: number;
  total: number;
}

type CourseSortDirection = 'asc' | 'desc';
type CourseSyncStatus = 'not_placed' | 'disabled' | 'syncing' | 'active' | 'global';
type PlacementStatusFilter = 'all' | 'placed' | 'unplaced';
type CourseSyncStatusFilter = 'all' | 'enabled' | 'disabled';
type PlacementRowActionKey = 'agentSettings' | 'openPlacement' | 'syncedContent' | 'viewStructure' | 'refreshCourse' | 'removePlacement';
type PlacementRowMenuItem = NbMenuItem & { key: PlacementRowActionKey };

/** Templates whose runtime supports course-scoped retrieval for synced
 *  Moodle content. Other templates still receive the synced content but
 *  can't filter on `moodle_course_id` — we warn the user via tooltip. */
const COURSE_SYNC_FILTER_COMPATIBLE_TEMPLATES = new Set<string>([
  'alpha_tutor',
  'alpha_flexible',
]);

@Component({
  selector: 'app-placements-site-card',
  templateUrl: './placements-site-card.component.html',
  styleUrl: './placements-site-card.component.scss',
})
export class PlacementsSiteCardComponent implements OnInit, OnDestroy {
  @Input() site!: LmsRegistration;
  /** All client-owned KBs / data sources — feeds the inline KB
   *  multi-select on each row. Owned by the parent so it's loaded
   *  exactly once and shared across the (possibly multiple) sites. */
  @Input() dataSources: DataSource[] = [];
  /** All dozent-bots in the client — feeds the per-row dozent select. */
  @Input() dozentBots: DozentBot[] = [];

  /** Self-heal signal: emitted when the backend tells us this site
   * UUID has been superseded (410 with code `site_superseded`).
   * The parent reloads its `lmsSites` array so the dead UUID drops
   * out, a fresh card spawns with the active UUID, and the user
   * sees the data they expected without any manual action. */
  @Output() siteSuperseded = new EventEmitter<{ oldId: string; activeId: string | null }>();

  loading = true;
  refreshing = false;
  error: string | null = null;
  /** Soft state from al-tutor when the registration pre-dates the
   * v3.0+ Moodle plugin (`moodle_public_key` NULL). Renders an inline
   * upgrade hint on the site card instead of an error toast. */
  pluginOutdated = false;
  /** True when al-tutor served canonical fallback data because the
   * outbound channel is blocked/gated. Course list and placements
   * still render normally; we just show a one-line banner with the
   * known issue + a "contact us" pointer. Detail level deliberately
   * kept low — anything verbose belongs in the support channel, not
   * the site card. */
  pluginDegraded = false;
  pluginHealthStatus: string | null = null;

  /** placements that currently exist on this site */
  placements: LmsPlacement[] = [];
  /** all Moodle courses returned by list_courses */
  courses: MoodleCourse[] = [];
  /** join: every Moodle course + its placement (if any) */
  rows: CourseRow[] = [];
  /** tutors of the caller's client — for bot dropdowns */
  tutors: Tutor[] = [];

  /** Bot-templates by id — used to look up the template name from a
   *  tutor's `template_id` so we can gate the dozent-link UI on
   *  templates that actually support it (alpha_tutor / alpha_flexible).
   *  Sourced from the global cache in TutorService.tutorTemplates$. */
  private templatesById = new Map<string, TutorTemplate>();

  /** text currently typed into the search field */
  filterInput = '';
  /** search term currently applied to server-side filtering */
  filter = '';
  courseSortDirection: CourseSortDirection = 'asc';
  coursePage = 0;
  coursePageSize = 25;
  courseTotal = 0;
  readonly pageSizeOptions = [25, 50, 100];
  categoryFilter: number | null = null;
  categoryOptions: Array<{ id: number; label?: string | null; name?: string | null; path?: string | null; count?: number }> = [];
  placementStatusFilter: PlacementStatusFilter = 'all';
  courseSyncStatusFilter: CourseSyncStatusFilter = 'all';
  selectedCourseIds = new Set<number>();
  private selectedCourseById = new Map<number, MoodleCourse>();
  bulkTutorId: string | null = null;
  selectingFiltered = false;
  bulkPlacing = false;
  bulkSyncing = false;
  bulkUnsyncing = false;
  bulkStructureParsing = false;
  bulkRemoving = false;
  /** Ticks of the combined Kursdaten/Didaktik step. Sync is the common case
   *  and starts ticked; Didaktik costs money, so it does not. */
  bulkRunSync = true;
  bulkRunDidactics = false;
  /** Set while a combined run is in flight so the Didaktik half starts only
   *  once the sync job has queued the courses it parses. */
  private bulkRunDidacticsAfterSync = false;
  bulkProgress: BulkProgress | null = null;
  bulkProgressStatus: string | null = null;

  private destroy$ = new Subject<void>();
  private readonly selectionPageSize = 100;
  private readonly bulkBatchSize = 25;
  private selectionLoadId = 0;
  activePreviewCourseId: number | null = null;

  moodleSiteTitle(): string {
    const raw = this.site?.moodle_site_url || this.site?.display_name || this.site?.moodle_site_identifier || '';
    try {
      return new URL(raw).hostname || raw;
    } catch {
      return raw.replace(/^https?:\/\//, '').replace(/\/+$/, '') || this.site?.display_name || '';
    }
  }
  private readinessRetryHandle: ReturnType<typeof setTimeout> | null = null;
  /** Offenes Verteil-Modal (Orbi-Schwarm) des laufenden Platzieren/Sync. */
  private distributeDialogRef: NbDialogRef<OrbiDistributeDialogComponent> | null = null;
  private readinessAttempts = 0;
  private readonly maxReadinessAttempts = 8;
  private readonly readinessRetryDelayMs = 1500;
  openRowMenuCourseId: number | null = null;

  constructor(
    private lms: LmsRegistryService,
    private tutorService: TutorService,
    private dialog: NbDialogService,
    private dialogSuppression: DialogSuppressionService,
    private toastr: NbToastrService,
    private t: TranslateService,
    private usageService: UsageService,
    private courseSyncTracker: CourseSyncJobTrackerService,
    private organisationService: OrganisationService,
    private dozentService: DozentService,
    private router: Router,
    private chatPreviewService: ChatPreviewService,
    private moodleService: MoodleService,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    // Tutors are client-scoped — usually already cached.
    this.tutorService.tutors$
      .pipe(takeUntil(this.destroy$))
      .subscribe((all) => (this.tutors = all));
    if (this.tutorService.tutors$.value.length === 0) {
      this.tutorService.listTutors().pipe(takeUntil(this.destroy$)).subscribe();
    }

    // Bot-templates are global / loaded once at app boot. We rebuild
    // the id→template map on every emit so the dozent-toggle gating
    // updates if the list ever refreshes.
    this.tutorService.tutorTemplates$
      .pipe(takeUntil(this.destroy$))
      .subscribe((templates) => {
        this.templatesById = new Map(templates.map((t) => [t.id, t]));
      });
    if (this.tutorService.tutorTemplates$.value.length === 0) {
      this.tutorService
        .listTutorTemplates()
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }

    this.chatPreviewService.isChatActive$
      .pipe(takeUntil(this.destroy$))
      .subscribe((active) => {
        if (!active) {
          this.activePreviewCourseId = null;
        }
      });

    this.load();
  }

  /** True if the tutor's template supports dozent-link routing
   *  (alpha_tutor / alpha_flexible). Used by the template to disable
   *  the dozent toggle + select for incompatible templates so users
   *  can't trigger the backend's 400. Falsy `botId` falls through to
   *  `false` so the toggle stays disabled until a tutor is picked. */
  isDozentCompatibleTutor(botId: string | null | undefined): boolean {
    if (!botId) return false;
    const tutor = this.tutors.find((t) => t.id === botId);
    if (!tutor?.template_id) return false;
    const template = this.templatesById.get(tutor.template_id);
    if (!template) return false;
    return DOZENT_COMPATIBLE_TUTOR_TEMPLATES.has(template.name);
  }

  /** True when the tutor's template supports course-scoped retrieval.
   *  The sync itself works for any template — only the retrieval filter
   *  is template-gated. */
  isCourseSyncFilterCompatible(botId: string | null | undefined): boolean {
    if (!botId) return false;
    const tutor = this.tutors.find((t) => t.id === botId);
    if (!tutor?.template_id) return false;
    const template = this.templatesById.get(tutor.template_id);
    if (!template) return false;
    return COURSE_SYNC_FILTER_COMPATIBLE_TEMPLATES.has(template.name);
  }

  tutorById(botId: string | null | undefined): Tutor | undefined {
    if (!botId) return undefined;
    return this.tutors.find((t) => t.id === botId);
  }

  dozentBotById(botId: string | null | undefined): DozentBot | undefined {
    if (!botId) return undefined;
    return this.dozentBots.find((d) => d.id === botId);
  }

  tutorVisualName(tutor: Tutor | null | undefined): string | null {
    const visualName = (tutor?.visual_name || '').trim();
    if (!visualName) return null;

    const internalName = (tutor?.name || '').trim();
    return visualName === internalName ? null : visualName;
  }

  tutorFullLabel(tutor: Tutor | null | undefined): string {
    if (!tutor) return '';
    const visualName = this.tutorVisualName(tutor);
    return visualName ? `${tutor.name} · ${visualName}` : tutor.name;
  }

  courseCategoryLabel(course: MoodleCourse): string {
    return (
      (course.category_path || course.category_name || '').trim()
      || this.t.instant('pages.tutors.placements.lms.filters.unnamedCategory')
    );
  }

  // ─── Queue state per course ────────────────────────────────────────
  //
  // The two toggles say what is switched on. They cannot say whether the
  // work behind them is still running, finished or dead — that lives in
  // al-filemanager's queue, so the row asks it directly and keeps asking
  // while anything is moving.

  /** Latest queue state per al-filemanager course id. */
  jobStatusByCourse = new Map<string, CourseJobStatus>();
  private jobStatusTimer: ReturnType<typeof setTimeout> | null = null;

  /** How often to re-ask while something is running. Slow enough to be
   *  invisible in the logs of a site with many open portals, fast enough
   *  that a finished parse does not sit unnoticed. */
  private static readonly JOB_STATUS_POLL_MS = 10000;

  private localCourseIds(): string[] {
    const ids: string[] = [];
    for (const row of this.rows) {
      const id = row.placement?.course_id;
      if (id && !ids.includes(id)) ids.push(id);
    }
    return ids;
  }

  /** Read the queue once, and schedule the next read only while a lane is
   *  actually moving — an idle table must not poll forever. */
  refreshJobStatus(): void {
    this.clearJobStatusPoll();
    const courseIds = this.localCourseIds();
    if (courseIds.length === 0) {
      this.jobStatusByCourse.clear();
      return;
    }
    this.lms
      .getCourseJobStatus(courseIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (entries) => {
          const next = new Map<string, CourseJobStatus>();
          for (const entry of entries) next.set(entry.course_id, entry);
          this.jobStatusByCourse = next;
          if (this.anyJobRunning) {
            this.jobStatusTimer = setTimeout(
              () => this.refreshJobStatus(),
              PlacementsSiteCardComponent.JOB_STATUS_POLL_MS,
            );
          }
        },
        // Silent: this is decoration on a table that works without it.
        error: () => undefined,
      });
  }

  private clearJobStatusPoll(): void {
    if (this.jobStatusTimer) {
      clearTimeout(this.jobStatusTimer);
      this.jobStatusTimer = null;
    }
  }

  get anyJobRunning(): boolean {
    for (const entry of this.jobStatusByCourse.values()) {
      if (entry.content.state === 'running' || entry.didactics.state === 'running') {
        return true;
      }
    }
    return false;
  }

  jobLane(row: CourseRow, lane: 'content' | 'didactics'): CourseJobLane | null {
    const courseId = row.placement?.course_id;
    if (!courseId) return null;
    const entry = this.jobStatusByCourse.get(courseId);
    if (!entry) return null;
    const value = entry[lane];
    return value && value.state !== 'idle' ? value : null;
  }

  jobLaneIcon(state: CourseJobState): string {
    if (state === 'running') return 'loader-outline';
    if (state === 'failed') return 'alert-triangle-outline';
    return 'checkmark-circle-2-outline';
  }

  /** Tooltip carries the queue's own error text when there is one — a red
   *  dot that cannot say why is a support ticket. */
  jobLaneTooltip(lane: CourseJobLane): string {
    if (lane.state === 'failed' && lane.error) return lane.error;
    return this.t.instant(`pages.tutors.placements.lms.jobState.${lane.state}`);
  }

  courseSyncStatus(row: CourseRow): CourseSyncStatus {
    if (row.savingSync) return 'syncing';
    if (this.autoCourseSyncCoversRow(row)) return 'global';
    return row.edit.courseSyncEnabled ? 'active' : 'disabled';
  }

  effectiveCourseSyncEnabled(row: CourseRow): boolean {
    return this.autoCourseSyncCoversRow(row) || row.edit.courseSyncEnabled;
  }

  courseSyncStatusKey(row: CourseRow): string {
    return `pages.tutors.placements.lms.courseSyncStatus.${this.courseSyncStatus(row)}`;
  }

  courseSyncStatusTooltipKey(row: CourseRow): string {
    return `${this.courseSyncStatusKey(row)}Tooltip`;
  }

  courseSyncToggleTooltipKey(row: CourseRow): string {
    if (this.autoCourseSyncCoversRow(row)) {
      return 'pages.tutors.placements.lms.siteAutoSyncCourseTooltip';
    }
    return 'pages.tutors.placements.lms.courseSyncFilterHint';
  }

  rowMenuTag(row: CourseRow): string {
    return `${this.rowMenuTagPrefix()}${row.course.id}`;
  }

  rowActionItems(row: CourseRow): PlacementRowMenuItem[] {
    const items: PlacementRowMenuItem[] = [
      {
        key: 'agentSettings',
        title: this.t.instant('pages.tutors.placements.lms.rowActions.agentSettings'),
        icon: { icon: 'aiMagic', pack: 'al' },
        hidden: !row.edit.botId,
        data: { action: 'agentSettings', courseId: row.course.id },
      },
      {
        key: 'openPlacement',
        title: this.t.instant('pages.tutors.placements.lms.rowActions.openPlacementCard'),
        icon: 'edit-outline',
        data: { action: 'openPlacement', courseId: row.course.id },
      },
      {
        key: 'syncedContent',
        title: this.t.instant('pages.tutors.placements.lms.rowActions.syncedContent'),
        icon: 'list-outline',
        // Only meaningful while the course is being synced — either via
        // the per-placement toggle or the instance-wide auto-sync.
        hidden: !(
          this.autoCourseSyncCoversRow(row) ||
          !!this.placementForCourse(row.course.id)?.course_sync_enabled
        ),
        data: { action: 'syncedContent', courseId: row.course.id },
      },
      {
        key: 'viewStructure',
        title: this.t.instant('pages.tutors.placements.lms.rowActions.viewStructure'),
        icon: 'book-open-outline',
        // The parsed Kurs-Didaktik (chapters/objectives/learning path). Shown
        // whenever a synced structure could exist; the dialog empty-states if
        // the course hasn't been analysed yet. Needs the canonical uuid.
        hidden: !row.course.uuid || !(
          this.autoCourseSyncCoversRow(row) ||
          !!this.placementForCourse(row.course.id)?.course_sync_enabled
        ),
        data: { action: 'viewStructure', courseId: row.course.id },
      },
      {
        key: 'refreshCourse',
        title: this.t.instant('pages.tutors.placements.lms.rowActions.refreshCourse'),
        icon: row.refreshingCourse ? 'sync-outline' : 'refresh-outline',
        hidden: row.refreshingCourse || !row.course.uuid,
        data: { action: 'refreshCourse', courseId: row.course.id },
      },
    ];
    if (this.rowHasAgentPlacement(row)) {
      items.push({
        key: 'removePlacement',
        title: this.t.instant('pages.tutors.placements.lms.rowActions.removePlacement'),
        icon: 'trash-2-outline',
        data: { action: 'removePlacement', courseId: row.course.id },
      });
    }
    return items;
  }

  ngOnDestroy(): void {
    this.clearReadinessRetry();
    // Ohne diese Karte kommt kein Abschlusssignal mehr (Platzieren läuft im
    // Browser, der Sync-Status wird von hier abgefragt) — ein noch laufendes
    // Modal hinge sonst für immer. Ein fertiges bleibt stehen.
    if (this.distributeDialogRef && !this.distributeDialogRef.componentRef?.instance?.done) {
      this.distributeDialogRef.close();
    }
    this.rowMenuScroll?.unsubscribe();
    this.clearJobStatusPoll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Resolve a backend error into a human-readable message string.
   *
   * al-tutor's outbound-Moodle paths send a structured `detail` object
   *   { code: string, message: string, url?, status?, body? }
   * for cases the portal should localize (plugin not found, signature
   * rejected, generic plugin failure, etc.). Plain-string details
   * coming from older endpoints continue to render verbatim. The
   * `fallbackKey` is used when neither path produces anything useful.
   */
  private resolveBackendError(err: any, fallbackKey: string): string {
    const detail = err?.error?.detail;
    // Legacy: string detail surfaces directly.
    if (typeof detail === 'string') {
      return detail || this.t.instant(fallbackKey);
    }
    // New structured detail: map known codes to i18n keys.
    if (detail && typeof detail === 'object' && typeof detail.code === 'string') {
      const code: string = detail.code;
      const i18nKey = `pages.tutors.placements.lms.errors.${this.codeToI18nKey(code)}`;
      const translated = this.t.instant(i18nKey, {
        url: detail.url || '',
        status: detail.status ?? '',
      });
      // ngx-translate returns the key when missing — fall back to the
      // backend's `message` so ops still sees something useful.
      if (translated !== i18nKey) return translated;
      return detail.message || this.t.instant(fallbackKey);
    }
    return this.t.instant(fallbackKey);
  }

  private codeToI18nKey(code: string): string {
    // snake_case → camelCase mapping to match existing i18n key style.
    return code.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  }

  private rowMenuTagPrefix(): string {
    return 'placement-row-';
  }

  visibleRowActionItems(row: CourseRow): PlacementRowMenuItem[] {
    return this.rowActionItems(row).filter((item) => !item.hidden);
  }

  rowActionIcon(item: PlacementRowMenuItem): string {
    const icon = item.icon as string | { icon?: string; pack?: string } | undefined;
    return typeof icon === 'string' ? icon : icon?.icon || 'more-vertical-outline';
  }

  rowActionIconPack(item: PlacementRowMenuItem): string {
    const icon = item.icon as string | { icon?: string; pack?: string } | undefined;
    return typeof icon === 'string' ? 'eva' : icon?.pack || 'eva';
  }

  rowMenuOpen(row: CourseRow): boolean {
    return this.openRowMenuCourseId === Number(row.course.id);
  }

  /** Das Zeilenmenü öffnet im CDK-Overlay statt absolut in der Zelle — dort
   *  schnitt es der waagerechte Scrollrahmen der Tabelle ab (`overflow-x:
   *  auto` erzwingt auch `overflow-y`); bei einem einzigen Treffer im Filter
   *  blieb vom Menü nur der letzte Eintrag übrig (Marcel, 27.09.2026). Unter
   *  dem ⋮, rechtsbündig; ist unten kein Platz, darüber. Nebular lenkt den
   *  Overlay-Container in `nb-layout` um — das Menü erbt CI-Tokens und die
   *  Overlay-Z-Index-Staffel. Gleiches Muster wie `app-table`. */
  readonly rowMenuPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
  ];

  /** Solange ein Zeilenmenü offen ist: jedes Scrollen schließt es. */
  private rowMenuScroll?: Subscription;

  toggleRowMenu(row: CourseRow, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const courseId = Number(row.course.id);
    if (this.openRowMenuCourseId === courseId) {
      this.closeRowMenu();
    } else {
      this.openRowMenuCourseId = courseId;
      this.closeRowMenuOnScroll();
    }
  }

  closeRowMenu(): void {
    this.openRowMenuCourseId = null;
    this.rowMenuScroll?.unsubscribe();
    this.rowMenuScroll = undefined;
  }

  /** Das Overlay folgt seiner Zeile nur beim Scrollen der Seite (CDK hört auf
   *  `document`-Scroll). Scrollt die Tabelle waagerecht, bliebe das Menü
   *  stehen und zeigte neben einer anderen Stelle — deshalb schließt jedes
   *  Scrollen außerhalb des Menüs es, wie ein natives Menü. `capture`, weil
   *  Scroll-Ereignisse nicht aufsteigen. */
  private closeRowMenuOnScroll(): void {
    if (this.rowMenuScroll) return;
    this.zone.runOutsideAngular(() => {
      this.rowMenuScroll = fromEvent(document, 'scroll', { capture: true, passive: true }).subscribe(
        (event) => {
          const target = event.target;
          if (target instanceof Element && target.closest('.site-card-row-menu-popover')) {
            return;
          }
          this.zone.run(() => this.closeRowMenu());
        },
      );
    });
  }

  /** Das Menü liegt im Overlay — also nicht mehr der nächste Tab-Stopp nach
   *  dem ⋮. Der Fokus springt auf den ersten Eintrag; erst im nächsten Takt,
   *  weil das `*ngFor` im Overlay erst im folgenden Durchlauf rendert. */
  onRowMenuAttached(overlay: CdkConnectedOverlay): void {
    setTimeout(() => {
      overlay.overlayRef?.overlayElement
        .querySelector<HTMLElement>('.site-card-row-menu-popover__item')
        ?.focus({ preventScroll: true });
    });
  }

  /** Das Overlay hat sich gelöst (Escape schließt es selbst). Zustand nur
   *  nachziehen, wenn es das Menü der offenen Zeile war — wechselt man von
   *  Zeile A zu B, löst sich A, während B schon offen ist. */
  onRowMenuDetached(row: CourseRow, origin: CdkOverlayOrigin): void {
    if (this.rowMenuOpen(row)) {
      this.closeRowMenu();
      (origin.elementRef.nativeElement as HTMLElement).focus({ preventScroll: true });
    }
  }

  /** Die Einträge werden bei jedem Durchlauf neu gebaut; ohne `trackBy`
   *  ersetzte Angular die Knöpfe laufend, und ein Klick zwischen `mousedown`
   *  und `mouseup` ginge verloren (CLAUDE.md Regel 18). */
  trackRowMenuItem(_index: number, item: PlacementRowMenuItem): string {
    return item.key;
  }

  @HostListener('document:click')
  closeRowMenuOnDocumentClick(): void {
    this.closeRowMenu();
  }

  private actionFromMenuItem(item: NbMenuItem, row: CourseRow): PlacementRowActionKey | undefined {
    const keyedItem = item as NbMenuItem & { key?: PlacementRowActionKey };
    const directAction = (item.data?.action || keyedItem.key) as PlacementRowActionKey | undefined;
    if (directAction) return directAction;
    return this.rowActionItems(row).find((candidate) => candidate.title === item.title)?.key;
  }

  onPlacementRowAction(row: CourseRow, item: PlacementRowMenuItem, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeRowMenu();
    const action = this.actionFromMenuItem(item, row);
    if (!action) return;

    switch (action) {
      case 'agentSettings':
        this.openAgentSettings(row);
        break;
      case 'openPlacement':
        this.openCreate(row.course, this.agentPlacementForCourse(row.course.id));
        break;
      case 'syncedContent':
        this.openSyncedContent(row);
        break;
      case 'viewStructure':
        this.openCourseStructure(row);
        break;
      case 'refreshCourse':
        this.refreshCourse(row);
        break;
      case 'removePlacement':
        this.onDelete(row);
        break;
      default:
        break;
    }
  }

  private resolveCourseSyncError(err: any, fallbackKey = 'toasts.error'): string {
    const detail = err?.error?.detail;
    if (detail === 'course_sync_requires_moodle_source') {
      return this.t.instant(
        'pages.tutors.placements.lms.errors.courseSyncWrongSource',
      );
    }
    if (detail === 'placement_has_no_moodle_course_id') {
      return this.t.instant(
        'pages.tutors.placements.lms.errors.placementMissingMoodleCourse',
      );
    }
    if (detail === 'filemanager_bucket_create_failed') {
      return this.t.instant(
        'pages.tutors.placements.lms.errors.bucketCreateFailed',
      );
    }
    if (detail === 'filemanager_moodle_sync_failed') {
      return this.t.instant(
        'pages.tutors.placements.lms.errors.moodleSyncFailed',
      );
    }
    if (detail === 'filemanager_moodle_bulk_sync_failed') {
      return this.t.instant(
        'pages.tutors.placements.lms.bulkSyncFailedToast',
      );
    }
    if (detail?.error_code === 'moodle_course_quota_exceeded') {
      return this.t.instant(
        'pages.tutors.placements.lms.errors.moodleCourseQuotaExceeded',
        {
          limit: detail.quota_limit ?? 0,
          remaining: detail.remaining ?? 0,
          requested: detail.requested ?? 1,
        },
      );
    }
    return this.resolveBackendError(err, fallbackKey);
  }

  /** i18n key for the degraded-state banner. Falls back to a generic
   * "verbindung gerade nicht stabil"-class text when the specific
   * health status isn't mapped — covers `unknown`, future statuses,
   * or a transient untyped error. */
  get degradedBannerKey(): string {
    const root = 'pages.tutors.placements.lms.healthBanner';
    const status = this.pluginHealthStatus || 'unknown';
    const key = this.codeToI18nKey(status);
    const candidate = `${root}.${key}`;
    return this.t.instant(candidate) === candidate ? `${root}.unknown` : candidate;
  }

  private clearReadinessRetry(): void {
    if (this.readinessRetryHandle) {
      clearTimeout(this.readinessRetryHandle);
      this.readinessRetryHandle = null;
    }
  }

  private isTransientReadinessStatus(status?: string | null): boolean {
    return [
      'site_token_unavailable',
      'token_missing',
      'token_revoked',
      'moodle_ws_error',
      'unreachable',
      'http_error',
      'unknown',
    ].includes(status || '');
  }

  private shouldWaitForReadiness(resp: MoodleCourseListResponse): boolean {
    if (this.refreshing) return false;
    if (resp.plugin_status !== 'degraded') return false;
    if (!this.isTransientReadinessStatus(resp.health_status)) return false;
    return this.readinessAttempts < this.maxReadinessAttempts;
  }

  private scheduleReadinessRetry(): void {
    this.clearReadinessRetry();
    this.readinessAttempts += 1;
    this.readinessRetryHandle = setTimeout(() => {
      this.readinessRetryHandle = null;
      this.load(true);
    }, this.readinessRetryDelayMs);
  }

  private load(refresh = false): void {
    this.clearReadinessRetry();
    this.loading = !this.refreshing;
    this.error = null;
    this.pluginOutdated = false;
    this.pluginDegraded = false;
    this.pluginHealthStatus = null;

    forkJoin({
      placements: this.lms.listSitePlacements(this.site.id, { refresh }),
      coursesResp: this.lms.listSiteCourses(
        this.site.id,
        this.courseListParams(this.coursePage * this.coursePageSize, this.coursePageSize, refresh),
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ placements, coursesResp }) => {
          this.placements = placements;
          const resp = coursesResp as MoodleCourseListResponse;
          const list = resp.courses || [];
          this.courses = list;
          this.courseTotal = resp.total ?? list.length;
          this.categoryOptions = resp.categories || [];
          // Soft signal from al-tutor: registration pre-dates the
          // v3.0+ plugin (`moodle_public_key` is NULL) → no outbound
          // call possible. Render an inline upgrade-hint instead of
          // an error toast.
          this.pluginOutdated = resp.plugin_status === 'outdated';
          // Outbound channel gated → courses are served from canonical
          // fallback. Banner copy depends on the specific health_status
          // (host blocked / WAF / upgrade-pending / signature etc.).
          this.pluginDegraded = resp.plugin_status === 'degraded';
          this.pluginHealthStatus = resp.health_status || null;
          this.buildRows();
          this.clampCoursePage();
          if (this.shouldWaitForReadiness(resp)) {
            this.loading = true;
            this.refreshing = false;
            this.pluginDegraded = false;
            this.pluginHealthStatus = null;
            this.scheduleReadinessRetry();
            return;
          }
          this.readinessAttempts = 0;
          this.loading = false;
          this.refreshing = false;
          // The rows are on screen; ask the queue what it is doing for them.
          this.refreshJobStatus();
        },
        error: (err) => {
          // Self-heal: 410 + code `site_superseded` means our cached
          // registration UUID is stale (the Moodle admin re-registered
          // the plugin, the old row is now disabled and a new row
          // exists). `site_disconnected` means Moodle no longer accepts
          // the old plugin key for this row. Don't render a raw HTTP
          // error — emit upward so the
          // parent reloads `lmsSites`, the *ngFor's trackBy on id
          // tears down THIS card, and a fresh card with the active
          // UUID renders. Soft INFO toast so the user sees what
          // happened without a scary danger banner.
          const detail = err?.error?.detail;
          if (
            err?.status === 410 &&
            detail &&
            typeof detail === 'object' &&
            ['site_superseded', 'site_disconnected'].includes(detail.code)
          ) {
            this.siteSuperseded.emit({
              oldId: this.site.id,
              activeId: detail.active_site_id ?? null,
            });
            this.toastr.info(
              this.t.instant(
                detail.code === 'site_disconnected'
                  ? 'pages.tutors.placements.lms.siteDisconnectedAutoHealed'
                  : 'pages.tutors.placements.lms.siteSupersededAutoHealed',
              ),
              '',
              { duration: 5000 },
            );
            // Stop loading state — the parent will rebuild the card.
            this.loading = false;
            this.refreshing = false;
            return;
          }
          this.placements = [];
          this.buildRows();
          this.clampCoursePage();
          this.error = this.resolveBackendError(
            err,
            'pages.tutors.placements.lms.loadError',
          );
          this.readinessAttempts = 0;
          this.loading = false;
          this.refreshing = false;
        },
      });
  }

  private buildRows(): void {
    const placementByCourseId: Record<number, LmsPlacement> = {};
    for (const p of this.placements) {
      if (
        p.moodle_course_id != null
        && (p.placement_scope || 'course') === 'course'
        && !this.isSyncOnlyPlacement(p)
      ) {
        placementByCourseId[p.moodle_course_id] = this.preferredCoursePlacement(
          placementByCourseId[p.moodle_course_id],
          p,
        );
      }
    }
    this.rows = this.courses.map((course) => {
      const placement = placementByCourseId[course.id];
      const tutor = placement?.bot_id
        ? this.tutors.find((t) => t.id === placement.bot_id)
        : undefined;
      const initial = {
        botId: placement?.bot_id || null,
        kbIds: [...(tutor?.tutor_data_source_ids || tutor?.data_source_ids || [])],
        dozentEnabled: !!tutor?.attached_dozent_id,
        dozentBotId: tutor?.attached_dozent_id || null,
        courseSyncEnabled: !!placement?.course_sync_enabled,
        structureParseEnabled: !!placement?.structure_parse_enabled,
      };
      return {
        course,
        placement,
        edit: { ...initial, kbIds: [...initial.kbIds] },
        initial,
        saving: false,
        refreshingCourse: false,
        savingSync: false,
        savingStructureParse: false,
      } as CourseRow;
    });
  }

  private placementForCourse(courseId: number): LmsPlacement | undefined {
    return this.placements
      .filter((p) => (
        p.moodle_course_id === courseId
        && (p.placement_scope || 'course') === 'course'
        && !this.isSyncOnlyPlacement(p)
      ))
      .reduce<LmsPlacement | undefined>(
        (current, placement) => this.preferredCoursePlacement(current, placement),
        undefined,
      );
  }

  agentPlacementForCourse(courseId: number): LmsPlacement | undefined {
    return this.placements
      .filter((p) => (
        p.moodle_course_id === courseId
        && (p.placement_scope || 'course') === 'course'
        && !this.isSyncOnlyPlacement(p)
      ))
      .reduce<LmsPlacement | undefined>(
        (current, placement) => this.preferredCoursePlacement(current, placement),
        undefined,
      );
  }

  private agentPlacementsForCourse(courseId: number): LmsPlacement[] {
    const seen = new Set<string>();
    return this.placements.filter((p) => {
      if (p.moodle_course_id !== courseId || this.isSyncOnlyPlacement(p)) return false;
      const placementId = this.placementId(p);
      if (!placementId) return false;
      if (seen.has(placementId)) return false;
      seen.add(placementId);
      return true;
    });
  }

  private inheritedModulePlacementsForCourse(
    courseId: number,
    previousBotId: string | null,
  ): LmsPlacement[] {
    return this.agentPlacementsForCourse(courseId).filter((placement) => (
      (placement.placement_scope || 'course') === 'module'
      && (placement.bot_id || null) === (previousBotId || null)
    ));
  }

  isSyncOnlyPlacement(placement?: LmsPlacement | null): boolean {
    return !!placement?.metadata?.['sync_only'];
  }

  isAgentPlacement(placement?: LmsPlacement | null): boolean {
    return !!placement && !this.isSyncOnlyPlacement(placement);
  }

  rowHasAgentPlacement(row: CourseRow): boolean {
    return this.isAgentPlacement(row.placement);
  }

  private preferredCoursePlacement(
    current: LmsPlacement | undefined,
    candidate: LmsPlacement,
  ): LmsPlacement {
    if (!current) return candidate;
    const currentAgent = this.isAgentPlacement(current);
    const candidateAgent = this.isAgentPlacement(candidate);
    if (currentAgent !== candidateAgent) {
      return candidateAgent ? candidate : current;
    }
    const currentSync = !!current.course_sync_enabled;
    const candidateSync = !!candidate.course_sync_enabled;
    if (currentSync !== candidateSync) {
      return candidateSync ? candidate : current;
    }
    return current;
  }

  toggleCourseSort(): void {
    this.courseSortDirection = this.courseSortDirection === 'asc' ? 'desc' : 'asc';
    this.coursePage = 0;
  }

  /** True when any of the inline-editable fields differs from the
   *  values we last loaded from the server. Drives the per-row Save
   *  button enabled-state. */
  isDirty(row: CourseRow): boolean {
    if (!row.placement) return false;
    const a = row.edit;
    const b = row.initial;
    if (a.botId !== b.botId) return true;
    if (a.dozentEnabled !== b.dozentEnabled) return true;
    if (a.dozentBotId !== b.dozentBotId) return true;
    if (a.kbIds.length !== b.kbIds.length) return true;
    const sa = new Set(a.kbIds);
    return b.kbIds.some((x) => !sa.has(x));
  }

  /** Per-row save. Bundles up to three independent calls:
   *    - PATCH /lms/placements/{uuid} with bot_id (when tutor changed)
   *    - PATCH /tutor/{id}              with tutor_data_source_ids (KB)
   *    - PUT/DELETE /tutor/{id}/dozent  for the dozent link
   *  All run in parallel via forkJoin; first failure surfaces in a
   *  toast but the rest still complete. After success we copy edit→
   *  initial so the row goes clean again without a full reload. */
  saveRow(row: CourseRow): void {
    if (!row.placement || row.saving || !this.isDirty(row)) return;
    row.saving = true;
    const calls: any[] = [];

    if (row.edit.botId !== row.initial.botId) {
      const placementUuid =
        row.placement.moodle_placement_uuid || row.placement.id;
      calls.push(
        this.lms.updatePlacement(placementUuid as string, {
          bot_id: row.edit.botId || null,
        }),
      );

      for (const placement of this.inheritedModulePlacementsForCourse(
        row.course.id,
        row.initial.botId,
      )) {
        const modulePlacementId = this.placementId(placement);
        if (!modulePlacementId) continue;
        calls.push(
          this.lms.updatePlacement(modulePlacementId, {
            bot_id: row.edit.botId || null,
          }),
        );
      }
    }

    // KB list change — applies tutor-globally (intentional: a tutor's
    // KB is per-tutor, not per-course, so editing on one row affects
    // every other row that shows the same tutor on next reload).
    const tutorId = row.edit.botId || row.initial.botId;
    if (tutorId) {
      const sa = new Set(row.initial.kbIds);
      const changed =
        row.edit.kbIds.length !== row.initial.kbIds.length ||
        row.edit.kbIds.some((x) => !sa.has(x));
      if (changed) {
        calls.push(
          this.tutorService.updateTutor({
            id: tutorId,
            tutor_data_source_ids: row.edit.kbIds,
          } as Partial<Tutor>),
        );
      }
    }

    // Dozent link — attach / change / detach.
    if (
      row.edit.dozentEnabled !== row.initial.dozentEnabled ||
      row.edit.dozentBotId !== row.initial.dozentBotId
    ) {
      if (tutorId) {
        if (row.edit.dozentEnabled && row.edit.dozentBotId) {
          calls.push(this.tutorService.attachDozent(tutorId, row.edit.dozentBotId));
        } else if (!row.edit.dozentEnabled) {
          calls.push(this.tutorService.detachDozent(tutorId));
        }
      }
    }

    if (calls.length === 0) {
      row.saving = false;
      return;
    }

    forkJoin(calls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          row.saving = false;
          row.initial = {
            botId: row.edit.botId,
            kbIds: [...row.edit.kbIds],
            dozentEnabled: row.edit.dozentEnabled,
            dozentBotId: row.edit.dozentBotId,
            courseSyncEnabled: row.initial.courseSyncEnabled,
            structureParseEnabled: row.initial.structureParseEnabled,
          };
          this.toastr.success(
            this.t.instant('pages.tutors.placements.lms.savedToast'),
            '',
          );
        },
        error: (err) => {
          row.saving = false;
          const detail = err?.error?.detail;
          // Structured detail (object with `code`) — Moodle outbound
          // failures route through here. Delegate to the shared
          // helper which knows how to localize `moodle_plugin_*`
          // codes. Falls through to the legacy string-code mapping
          // below for older endpoints that still send plain strings.
          if (detail && typeof detail === 'object' && typeof detail.code === 'string') {
            this.toastr.danger(this.resolveBackendError(err, 'toasts.error'), '');
            return;
          }
          // Map stable backend error_codes (see dozent_link_ops.py /
          // moodle_inbound_endpoints.py) to localized hints. Anything
          // we don't recognise falls back to the raw detail.
          let msg: string;
          if (detail === 'tutor_template_not_dozent_compatible') {
            msg = this.t.instant(
              'pages.tutors.placements.lms.errors.tutorTemplateNotDozentCompatible',
            );
          } else if (detail === 'target_template_not_dozent') {
            msg = this.t.instant(
              'pages.tutors.placements.lms.errors.targetTemplateNotDozent',
            );
          } else if (detail === 'remote_placement_disabled_by_site_admin') {
            msg = this.t.instant(
              'pages.tutors.placements.lms.errors.remoteDisabled',
            );
          } else if (detail === 'course_not_found_in_moodle') {
            msg = this.t.instant(
              'pages.tutors.placements.lms.errors.courseNotFound',
            );
          } else {
            msg = detail || this.t.instant('toasts.error');
          }
          this.toastr.danger(msg, '');
        },
      });
  }

  /** Per-row Kurs-Sync toggle. Commits immediately to the backend
   *  (independent of the Save button) — on success the toggle stays
   *  at the new value, on error we revert + toast. The actual Moodle
   *  import is async on the backend, so we return after the orchestration
   *  endpoint acks and the user sees a "started" toast. */
  onCourseSyncToggle(row: CourseRow, enabled: boolean): void {
    if (row.savingSync) return;
    if (this.autoCourseSyncCoversRow(row)) {
      this.toastr.info(
        this.t.instant('pages.tutors.placements.lms.siteAutoSyncCourseTooltip'),
        '',
      );
      return;
    }
    if (row.edit.courseSyncEnabled === enabled) return;
    const currentPlacement = row.placement || this.placementForCourse(row.course.id);
    if (!enabled && !currentPlacement) return;
    const previous = row.edit.courseSyncEnabled;
    row.edit.courseSyncEnabled = enabled;
    row.savingSync = true;

    // Widen to `Observable<unknown>` so the union of the two service
    // return shapes (enable vs disable response payloads) doesn't trip
    // TS2349 when piping. We only act on success/error here — the
    // specific payload shape is irrelevant to this caller.
    let obs$: Observable<unknown>;
    if (enabled && !currentPlacement) {
      obs$ = this.ensurePlacement(row.course, row.edit.botId).pipe(
        tap((placement) => {
          this.upsertLocalPlacement(placement);
          row.placement = placement;
          row.edit.botId = placement.bot_id || row.edit.botId || null;
          row.initial.botId = placement.bot_id || row.edit.botId || row.initial.botId || null;
        }),
        switchMap((placement) => {
          const placementId = this.placementId(placement);
          if (!placementId) {
            throw new Error('placement_missing_id');
          }
          return this.lms.enableCourseSync(placementId).pipe(
            map((result: any) => ({ ...result, placement })),
          );
        }),
      );
    } else {
      const placementId = (currentPlacement?.moodle_placement_uuid ||
        currentPlacement?.id) as string | undefined;
      if (!placementId) {
        row.savingSync = false;
        row.edit.courseSyncEnabled = previous;
        this.toastr.danger(this.t.instant('pages.tutors.placements.lms.errors.placementMissingMoodleCourse'), '');
        return;
      }
      obs$ = enabled
        ? this.lms.enableCourseSync(placementId)
        : this.lms.disableCourseSync(placementId);
    }

    obs$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: any) => {
        row.initial.courseSyncEnabled = enabled;
        if (enabled && result?.placement) {
          const placement = {
            ...result.placement,
            course_sync_enabled: true,
          } as LmsPlacement;
          this.upsertLocalPlacement(placement);
          row.placement = placement;
          row.edit.botId = placement.bot_id || row.edit.botId || null;
          row.initial.botId = placement.bot_id || row.edit.botId || null;
        } else if (currentPlacement) {
          currentPlacement.course_sync_enabled = enabled;
          row.placement = currentPlacement;
        }
        if (enabled && result?.job_id) {
          this.pollSingleCourseSyncStatus(result.job_id, row);
          return;
        }
        row.savingSync = false;
        this.toastr.success(
          this.t.instant(
            enabled
              ? 'pages.tutors.placements.lms.courseSyncEnabledToast'
              : 'pages.tutors.placements.lms.courseSyncDisabledToast',
          ),
          '',
        );
      },
      error: (err) => {
        row.savingSync = false;
        row.edit.courseSyncEnabled = previous;
        this.toastr.danger(this.resolveCourseSyncError(err), '');
      },
    });
  }

  /** Advanced sync (structure parse) is only meaningful once course-sync is
   *  effectively on for this row — no content is imported otherwise, so there
   *  is nothing to parse. The template disables the toggle accordingly. */
  structureParseAvailable(row: CourseRow): boolean {
    return this.effectiveCourseSyncEnabled(row);
  }

  effectiveStructureParse(row: CourseRow): boolean {
    return this.structureParseAvailable(row) && row.edit.structureParseEnabled;
  }

  structureParseTooltipKey(row: CourseRow): string {
    return this.structureParseAvailable(row)
      ? 'pages.tutors.placements.lms.structureParseHint'
      : 'pages.tutors.placements.lms.structureParseNeedsSync';
  }

  /** Toggle the per-placement `structure_parse_enabled` flag. Pure flag flip
   *  against a dedicated endpoint — no bulk sync, committed immediately like
   *  the course-sync toggle. Requires an existing placement (guaranteed while
   *  course-sync is on, which the toggle is gated on). */
  onStructureParseToggle(row: CourseRow, enabled: boolean): void {
    if (row.savingStructureParse) return;
    if (!this.structureParseAvailable(row)) return;
    if (row.edit.structureParseEnabled === enabled) return;
    if (enabled) {
      // The toggle already looks "on" after the click; mirror it so that
      // cancelling can put it back. `effectiveStructureParse` reads this flag.
      const previous = row.edit.structureParseEnabled;
      row.edit.structureParseEnabled = true;
      this.confirmDidacticsCost(
        'didactics-cost-course',
        () => this.applyStructureParseToggle(row, true),
        () => { row.edit.structureParseEnabled = previous; },
      );
      return;
    }
    this.applyStructureParseToggle(row, false);
  }

  /** Ask once before switching course didactics ON: it costs credits per
   *  teaching unit and runs for minutes. The operator may silence it, and
   *  that choice is remembered per user. Switching OFF is free — no dialog. */
  private confirmDidacticsCost(
    dialogKey: string,
    onConfirm: () => void,
    onCancel?: () => void,
  ): void {
    if (this.dialogSuppression.isSuppressed(dialogKey)) {
      onConfirm();
      return;
    }
    this.dialog
      .open(ConfirmDialogComponent, {
        context: {
          title: 'dialogs.didacticsCost.title',
          message: 'dialogs.didacticsCost.message',
          details: 'dialogs.didacticsCost.details',
          confirmLabel: 'dialogs.didacticsCost.confirm',
          cancelLabel: 'buttons.cancel',
          confirmStatus: 'primary',
          cancelStatus: 'basic',
          showSuppress: true,
        },
      })
      .onClose.subscribe((res: { confirmed?: boolean; suppress?: boolean } | undefined) => {
        if (!res?.confirmed) {
          onCancel?.();
          return;
        }
        if (res.suppress) this.dialogSuppression.suppress(dialogKey);
        onConfirm();
      });
  }

  private applyStructureParseToggle(row: CourseRow, enabled: boolean): void {
    const placement = row.placement || this.placementForCourse(row.course.id);
    const placementId = (placement?.moodle_placement_uuid || placement?.id) as
      | string
      | undefined;
    if (!placement || !placementId) {
      this.toastr.danger(
        this.t.instant('pages.tutors.placements.lms.errors.placementMissingMoodleCourse'),
        '',
      );
      return;
    }
    const previous = row.edit.structureParseEnabled;
    row.edit.structureParseEnabled = enabled;
    row.savingStructureParse = true;

    // Widen to Observable<unknown> so the union of the enable/disable
    // response shapes ({enabled:true|false}) doesn't trip TS2349 on
    // subscribe (same pattern as onCourseSyncToggle).
    const obs$: Observable<unknown> = enabled
      ? this.lms.enableStructureParse(placementId)
      : this.lms.disableStructureParse(placementId);

    obs$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        row.initial.structureParseEnabled = enabled;
        placement.structure_parse_enabled = enabled;
        row.placement = placement;
        this.upsertLocalPlacement(placement);
        row.savingStructureParse = false;
        this.toastr.success(
          this.t.instant(
            enabled
              ? 'pages.tutors.placements.lms.structureParseEnabledToast'
              : 'pages.tutors.placements.lms.structureParseDisabledToast',
          ),
          '',
        );
      },
      error: (err) => {
        row.savingStructureParse = false;
        row.edit.structureParseEnabled = previous;
        this.toastr.danger(this.resolveCourseSyncError(err), '');
      },
    });
  }

  private upsertLocalPlacement(placement: LmsPlacement): void {
    const index = this.placements.findIndex((candidate) => (
      candidate.id === placement.id
      || (
        !!candidate.moodle_placement_uuid
        && candidate.moodle_placement_uuid === placement.moodle_placement_uuid
      )
    ));
    if (index >= 0) {
      this.placements[index] = placement;
    } else {
      this.placements.push(placement);
    }
  }

  private pollSingleCourseSyncStatus(jobId: string, row: CourseRow): void {
    setTimeout(() => {
      this.lms.getCourseSyncBulkStatus(jobId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (job) => {
            if (job.status === 'COMPLETED') {
              row.savingSync = false;
              // COMPLETED heißt nur „Job durchgelaufen" — ob der Kurs ankam,
              // steht in failed_courses.
              if ((job.failed_courses || 0) > 0) {
                this.toastr.danger(
                  this.moodleService.translateCourseSyncError(job.course_errors?.[0])
                    || this.t.instant('dialogs.orbiDistribute.sync.failed'),
                  '',
                );
                this.load(false);
                return;
              }
              this.toastr.success(
                this.t.instant('pages.tutors.placements.lms.courseSyncEnabledToast'),
                '',
              );
              return;
            }
            if (job.status === 'FAILED') {
              row.savingSync = false;
              this.toastr.danger(
                job.error_message || this.t.instant('pages.tutors.placements.lms.bulkSyncFailedToast'),
                '',
              );
              this.load(false);
              return;
            }
            this.pollSingleCourseSyncStatus(jobId, row);
          },
          error: (err) => {
            row.savingSync = false;
            this.toastr.danger(this.resolveCourseSyncError(err), '');
            this.load(false);
          },
        });
    }, 3000);
  }

  get filteredRows(): CourseRow[] {
    const direction = this.courseSortDirection === 'asc' ? 1 : -1;
    return [...this.rows].sort((a, b) => {
      const aName = `${a.course.fullname || ''} ${a.course.shortname || ''}`.trim().toLocaleLowerCase();
      const bName = `${b.course.fullname || ''} ${b.course.shortname || ''}`.trim().toLocaleLowerCase();
      const byName = aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
      if (byName !== 0) return byName * direction;
      return (a.course.id - b.course.id) * direction;
    });
  }

  get pagedRows(): CourseRow[] {
    return this.filteredRows;
  }

  applyTextFilter(): void {
    const nextFilter = this.filterInput.trim();
    if (nextFilter === this.filter) return;
    this.filter = nextFilter;
    this.coursePage = 0;
    this.clearSelection();
    this.load(false);
  }

  clearTextFilter(): void {
    if (!this.filterInput && !this.filter) return;
    this.filterInput = '';
    this.filter = '';
    this.coursePage = 0;
    this.clearSelection();
    this.load(false);
  }

  onCourseFilterChange(): void {
    this.coursePage = 0;
    this.clearSelection();
    this.load(false);
  }

  onCoursePageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.courseTotal / this.coursePageSize) - 1, 0);
    this.coursePage = Math.max(0, Math.min(page, maxPage));
    this.load(false);
  }

  onCoursePageSizeChange(size: number): void {
    this.coursePageSize = Math.min(Math.max(size, 1), 100);
    this.coursePage = 0;
    this.load(false);
  }

  get selectedCount(): number {
    return this.selectedCourseIds.size;
  }

  /** Selected courses that already have a placement with an id — the only
   *  ones Kurs-Didaktik (structure parse) can be flipped on (it needs a
   *  placement + synced content). */
  get selectedStructureParseCount(): number {
    return this.selectedCourses().filter((course) => {
      const p = this.placementForCourse(course.id);
      return !!p && !!(p.moodle_placement_uuid || p.id);
    }).length;
  }

  private applyBulkEnableStructureParse(): void {
    const placements = this.selectedCourses()
      .map((c) => this.placementForCourse(c.id))
      .filter((p): p is LmsPlacement => !!p && !!(p.moodle_placement_uuid || p.id));
    if (placements.length === 0) {
      this.toastr.warning(
        this.t.instant('pages.tutors.placements.lms.structureParseNeedsSync'),
        '',
      );
      return;
    }
    const placementIds = placements
      .map((p) => (p.moodle_placement_uuid || p.id) as string);
    this.bulkStructureParsing = true;
    // One call instead of one per placement: the fan-out belongs on the
    // server, where the parses land on al-filemanager's durable queue even
    // if this tab is closed a second later.
    this.lms
      .enableStructureParseBulk(placementIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.bulkStructureParsing = false;
          for (const p of placements) {
            p.structure_parse_enabled = true;
            const row = this.rows.find(
              (r) => r.placement?.id === p.id || r.course.id === p.moodle_course_id,
            );
            if (row) {
              row.edit.structureParseEnabled = true;
              row.initial.structureParseEnabled = true;
              if (row.placement) row.placement.structure_parse_enabled = true;
            }
          }
          this.refreshJobStatus();
          this.toastr.success(
            this.t.instant('pages.tutors.placements.lms.structureParseBulkToast', {
              count: placements.length,
            }),
            '',
          );
        },
        error: () => {
          this.bulkStructureParsing = false;
          this.toastr.danger(
            this.t.instant('pages.tutors.placements.lms.structureParseBulkFailedToast'),
            '',
          );
        },
      });
  }

  get bulkBusy(): boolean {
    return this.selectingFiltered || this.bulkPlacing || this.bulkSyncing || this.bulkUnsyncing || this.bulkStructureParsing || this.bulkRemoving;
  }

  // ─── Kursdaten-Sync + Kurs-Didaktik as one step ────────────────────
  //
  // These were three buttons sitting next to each other with no stated
  // relationship, so running both meant pressing two of them and guessing
  // whether the order mattered (it does — Didaktik parses synced content).
  // Now the operator ticks what should happen and presses once.

  /** True while the combined run is in flight — either half counts. */
  get bulkRunning(): boolean {
    return this.bulkSyncing || this.bulkStructureParsing;
  }

  get bulkRunAnything(): boolean {
    return this.bulkRunSync || this.bulkRunDidactics;
  }

  /** Button label follows the ticks, so the press is never a surprise. */
  get bulkRunActionKey(): string {
    const base = 'pages.tutors.placements.lms.bulk.';
    if (this.bulkRunSync && this.bulkRunDidactics) return base + 'runBoth';
    if (this.bulkRunDidactics) return base + 'runDidacticsOnly';
    return base + 'runSyncOnly';
  }

  onBulkRunSyncChange(checked: boolean): void {
    this.bulkRunSync = checked;
    // Didaktik parses synced content, so it cannot be the only thing left
    // running for courses that have no sync at all.
    if (!checked && this.selectedSyncEnabledCount === 0) {
      this.bulkRunDidactics = false;
    }
  }

  onBulkRunDidacticsChange(checked: boolean): void {
    this.bulkRunDidactics = checked;
    // Ticking Didaktik for a selection that is not synced yet would flip a
    // flag and then wait for a sync nobody started. Tick Kursdaten along.
    if (checked && this.selectedSyncEnabledCount < this.selectedCount) {
      this.bulkRunSync = true;
    }
  }

  /** Run the ticked steps in the order they depend on each other. */
  bulkRunSelected(): void {
    if (this.bulkBusy || this.selectedCount === 0 || !this.bulkRunAnything) return;
    if (this.bulkRunDidactics) {
      // One confirmation for the whole run, asked before anything starts.
      this.confirmDidacticsCost('didactics-cost-bulk', () => this.startBulkRun());
      return;
    }
    this.startBulkRun();
  }

  private startBulkRun(): void {
    if (this.bulkRunSync) {
      // Didaktik is handed to `finishBulkSync` so it starts once the sync
      // job has actually queued the courses.
      this.bulkRunDidacticsAfterSync = this.bulkRunDidactics;
      this.bulkSyncSelected();
      return;
    }
    this.applyBulkEnableStructureParse();
  }

  get bulkProgressPercent(): number {
    if (!this.bulkProgress?.total) return 0;
    return Math.round((this.bulkProgress.done / this.bulkProgress.total) * 100);
  }

  get autoCourseSyncActive(): boolean {
    return !!this.site?.auto_course_sync_enabled;
  }

  /** Instance auto-sync restricted to courses with an active placement. */
  get autoCourseSyncPlacementsOnly(): boolean {
    return this.autoCourseSyncActive && this.site?.auto_course_sync_mode === 'placements';
  }

  /** Instance auto-sync in 'all' mode — every row is covered. */
  get autoCourseSyncCoversAll(): boolean {
    return this.autoCourseSyncActive && !this.autoCourseSyncPlacementsOnly;
  }

  /** Whether the instance-wide auto-sync applies to this specific row.
   *  In 'placements' mode only rows with a real block placement (any
   *  scope, sync_only helper rows excluded — mirrors the backend EXISTS
   *  filter) are covered; the rest keep their manual sync controls. */
  autoCourseSyncCoversRow(row: CourseRow): boolean {
    if (!this.autoCourseSyncActive) return false;
    if (!this.autoCourseSyncPlacementsOnly) return true;
    return this.placements.some(
      (p) => p.moodle_course_id === row.course.id && !this.isSyncOnlyPlacement(p),
    );
  }

  get selectedPlacedCount(): number {
    return this.selectedCourses().filter((course) => !!this.agentPlacementForCourse(course.id)).length;
  }

  get selectedSyncEnabledCount(): number {
    if (this.autoCourseSyncCoversAll) return 0;
    return this.selectedCourses().filter((course) => {
      const placement = this.placementForCourse(course.id);
      return !!placement?.course_sync_enabled;
    }).length;
  }

  isCourseSelected(course: MoodleCourse): boolean {
    return this.selectedCourseIds.has(course.id);
  }

  get allVisibleSelected(): boolean {
    if (this.pagedRows.length === 0) return false;
    const pageSelected = this.pagedRows.every((row) => this.isCourseSelected(row.course));
    if (this.courseTotal > this.pagedRows.length) {
      return pageSelected && this.selectedCourseIds.size >= this.courseTotal;
    }
    return pageSelected;
  }

  toggleCourseSelection(row: CourseRow, selected: boolean): void {
    if (selected) {
      this.selectedCourseIds.add(row.course.id);
      this.selectedCourseById.set(row.course.id, row.course);
    } else {
      this.selectedCourseIds.delete(row.course.id);
      this.selectedCourseById.delete(row.course.id);
    }
  }

  toggleFilteredSelection(selected: boolean): void {
    if (!selected) {
      this.clearSelection();
      return;
    }
    if (this.selectingFiltered) return;
    for (const row of this.pagedRows) {
      this.toggleCourseSelection(row, true);
    }
    if (this.courseTotal <= this.pagedRows.length) {
      return;
    }

    this.selectingFiltered = true;
    const loadId = ++this.selectionLoadId;
    this.fetchAllFilteredCourses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (courses) => {
          if (loadId !== this.selectionLoadId) return;
          for (const course of courses) {
            this.selectedCourseIds.add(course.id);
            this.selectedCourseById.set(course.id, course);
          }
          this.selectingFiltered = false;
        },
        error: (err) => {
          if (loadId !== this.selectionLoadId) return;
          this.selectingFiltered = false;
          this.clearSelection();
          this.toastr.danger(this.resolveBackendError(err, 'toasts.error'), '');
        },
      });
  }

  clearSelection(): void {
    this.selectionLoadId += 1;
    this.selectingFiltered = false;
    this.selectedCourseIds.clear();
    this.selectedCourseById.clear();
  }

  private selectedCourses(): MoodleCourse[] {
    return Array.from(this.selectedCourseById.values());
  }

  /** Verteil-Modal für „Platzieren" / „Kurs-Sync starten". Nicht
   *  wegklickbar, solange der Vorgang läuft — Platzieren läuft komplett im
   *  Browser und bräche beim Verlassen der Seite ab; geschlossen wird erst
   *  nach dem Ergebnis über den Knopf im Modal. */
  private openDistributeDialog(mode: OrbiDistributeMode): void {
    this.distributeDialogRef?.close();
    const ref = this.dialog.open(OrbiDistributeDialogComponent, {
      context: { mode },
      hasBackdrop: true,
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });
    this.distributeDialogRef = ref;
    ref.onClose.subscribe(() => {
      if (this.distributeDialogRef === ref) this.distributeDialogRef = null;
    });
  }

  /** Abschlusssignal ans Verteil-Modal — dieselben Zahlen wie im Toast.
   *  Beim Kurs-Sync gehört das Modal ab der `job_id` dem
   *  `CourseSyncJobTrackerService` (`distributeDialogRef` ist dann `null`,
   *  der Aufruf hier wirkungslos); der Tracker meldet das Ergebnis selbst. */
  private completeDistributeDialog(successCount: number, failedCount: number): void {
    const outcome = successCount === 0 ? 'failed' : failedCount > 0 ? 'partial' : 'success';
    this.distributeDialogRef?.componentRef?.instance?.complete({ outcome, successCount, failedCount });
  }

  private runBatched<T>(calls: Array<Observable<T>>): Observable<T[]> {
    const batches: Array<Array<Observable<T>>> = [];
    for (let i = 0; i < calls.length; i += this.bulkBatchSize) {
      batches.push(calls.slice(i, i + this.bulkBatchSize));
    }
    this.bulkProgress = { done: 0, total: calls.length };
    return from(batches).pipe(
      concatMap((batch) => forkJoin(batch).pipe(
        tap((results) => {
          if (this.bulkProgress) {
            this.bulkProgress = {
              ...this.bulkProgress,
              done: Math.min(this.bulkProgress.done + results.length, this.bulkProgress.total),
            };
          }
        }),
      )),
      reduce((all, batchResults) => [...all, ...batchResults], [] as T[]),
    );
  }

  private clearBulkProgress(): void {
    this.bulkProgress = null;
    this.bulkProgressStatus = null;
  }

  private setBulkProgressStatus(status?: string | null): void {
    const normalized = (status || '').toUpperCase();
    const key = normalized === 'QUEUED'
      ? 'queued'
      : normalized === 'PREPARING'
        ? 'preparing'
        : normalized === 'QUEUING'
          ? 'queuing'
          : normalized === 'IN_PROGRESS'
            ? 'inProgress'
            : null;
    this.bulkProgressStatus = key
      ? this.t.instant(`pages.tutors.placements.lms.bulk.status.${key}`)
      : null;
  }

  private courseListParams(offset: number, limit: number, refresh = false): {
    limit: number;
    offset: number;
    search: string;
    categoryId: number | null;
    placementStatus: PlacementStatusFilter;
    courseSyncStatus: CourseSyncStatusFilter;
    refresh: boolean;
  } {
    return {
      limit,
      offset,
      search: this.filter,
      categoryId: this.categoryFilter,
      placementStatus: this.placementStatusFilter,
      courseSyncStatus: this.autoCourseSyncCoversAll ? 'all' : this.courseSyncStatusFilter,
      refresh,
    };
  }

  private fetchAllFilteredCourses(): Observable<MoodleCourse[]> {
    const baseParams = this.courseListParams(0, this.selectionPageSize);
    return this.lms
      .listSiteCourses(this.site.id, baseParams)
      .pipe(
        switchMap((first) => {
          const firstCourses = first.courses || [];
          const total = first.total ?? firstCourses.length;
          const calls: Array<Observable<MoodleCourseListResponse>> = [];
          for (let offset = this.selectionPageSize; offset < total; offset += this.selectionPageSize) {
            calls.push(this.lms.listSiteCourses(this.site.id, { ...baseParams, offset }));
          }
          if (calls.length === 0) return of(firstCourses);
          return forkJoin(calls).pipe(
            map((pages) => [
              ...firstCourses,
              ...pages.flatMap((page) => page.courses || []),
            ]),
          );
        }),
      );
  }

  private placementId(placement: LmsPlacement): string | null {
    return (placement.moodle_placement_uuid || placement.id || null) as string | null;
  }

  private ensurePlacement(course: MoodleCourse, tutorId?: string | null): Observable<LmsPlacement> {
    const existing = this.placementForCourse(course.id);
    if (existing) {
      const placementId = this.placementId(existing);
      if (!placementId) return of(existing);
      if (!tutorId || existing.bot_id === tutorId) return of(existing);
      return this.lms.updatePlacement(placementId, { bot_id: tutorId });
    }
    return this.lms.createSitePlacement(this.site.id, {
      course_id: course.id,
      bot_id: tutorId || null,
    });
  }

  bulkPlaceSelected(): void {
    if (this.bulkBusy || this.selectedCount === 0) return;
    const courses = this.selectedCourses();
    if (courses.length === 0) return;
    this.bulkPlacing = true;
    this.openDistributeDialog('place');
    const calls = courses.map((course) => this.ensurePlacement(course, this.bulkTutorId).pipe(
      map((placement) => ({
        courseId: course.id,
        ok: true,
        placement,
      } as BulkPlaceResult)),
      catchError((error) => of({
        courseId: course.id,
        ok: false,
        error,
      } as BulkPlaceResult)),
    ));
    this.runBatched(calls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.bulkPlacing = false;
          this.clearBulkProgress();
          const successCount = results.filter((result) => result.ok).length;
          const failedCount = results.length - successCount;
          this.completeDistributeDialog(successCount, failedCount);

          if (successCount === 0) {
            const firstError = results.find((result) => !result.ok)?.error;
            this.toastr.danger(
              this.resolveBackendError(
                firstError,
                'pages.tutors.placements.lms.bulkPlacementFailedToast',
              ),
              '',
            );
            this.load(false);
            return;
          }

          if (failedCount > 0) {
            this.toastr.warning(
              this.t.instant('pages.tutors.placements.lms.bulkPlacementPartialToast', {
                success: successCount,
                failed: failedCount,
              }),
              '',
            );
          } else {
            this.toastr.success(
              this.t.instant('pages.tutors.placements.lms.bulkPlacementToast', {
                count: successCount,
              }),
              '',
            );
          }
          this.clearSelection();
          this.load(false);
        },
        error: (err) => {
          this.bulkPlacing = false;
          this.clearBulkProgress();
          this.completeDistributeDialog(0, courses.length);
          this.toastr.danger(this.resolveBackendError(err, 'toasts.error'), '');
        },
      });
  }

  bulkSyncSelected(): void {
    if (this.bulkBusy || this.selectedCount === 0) return;
    if (this.autoCourseSyncCoversAll) {
      this.toastr.info(
        this.t.instant('pages.tutors.placements.lms.siteAutoSyncBulkTooltip'),
        '',
      );
      return;
    }
    const courses = this.selectedCourses();
    const eligibleCourses = courses;
    if (eligibleCourses.length === 0) {
      this.toastr.warning(
        this.t.instant('pages.tutors.placements.lms.bulkSyncNeedsPlacement'),
        '',
      );
      return;
    }

    const newSyncCourses = eligibleCourses.filter((course) => (
      !this.placementForCourse(course.id)?.course_sync_enabled
    ));
    const courseUsage = this.usageService.usage$.value?.moodle_course_usage;
    const remaining = typeof courseUsage?.quota_limit === 'number'
      ? (courseUsage.quota_limit < 0
        ? null
        : Math.max((courseUsage.quota_limit || 0) - (courseUsage.current_count || 0), 0))
      : null;

    if (remaining !== null && newSyncCourses.length > remaining) {
      if (remaining <= 0) {
        this.toastr.warning(
          this.t.instant('pages.tutors.placements.lms.moodleCourseQuotaExceeded', {
            limit: courseUsage?.quota_limit ?? 0,
          }),
          '',
        );
        return;
      }

      this.dialog
        .open(ConfirmDialogComponent, {
          context: {
            title: 'pages.tutors.placements.lms.bulkSyncQuotaDialog.title',
            message: this.t.instant('pages.tutors.placements.lms.bulkSyncQuotaDialog.message', {
              selected: newSyncCourses.length,
              allowed: remaining,
            }),
            confirmLabel: this.t.instant('pages.tutors.placements.lms.bulkSyncQuotaDialog.confirm', {
              allowed: remaining,
            }),
            confirmStatus: 'primary',
          },
          hasBackdrop: true,
        })
        .onClose
        .pipe(takeUntil(this.destroy$))
        .subscribe((confirmed) => {
          if (!confirmed) return;
          const alreadyEnabled = eligibleCourses.filter((course) => (
            !!this.placementForCourse(course.id)?.course_sync_enabled
          ));
          this.runBulkSync([...alreadyEnabled, ...newSyncCourses.slice(0, remaining)]);
        });
      return;
    }

    this.runBulkSync(eligibleCourses);
  }

  private runBulkSync(courses: MoodleCourse[]): void {
    const rowsByCourseId = new Map(this.rows.map((row) => [row.course.id, row]));
    const calls: Observable<BulkSyncResult>[] = [];
    for (const course of courses) {
      const existing = this.placementForCourse(course.id);
      const row = rowsByCourseId.get(course.id);
      if (row) {
        row.savingSync = true;
      }
      if (existing) {
        calls.push(
          of(existing).pipe(
            map((placement) => {
              const placementId = this.placementId(placement);
              if (!placementId) {
                return {
                  courseId: course.id,
                  ok: false,
                  placement,
                  error: 'placement_missing_id',
                } as BulkSyncResult;
              }
              return {
                courseId: course.id,
                ok: true,
                placement,
              } as BulkSyncResult;
            }),
            catchError((error) => of({
              courseId: course.id,
              ok: false,
              error,
            } as BulkSyncResult)),
          ),
        );
      } else {
        calls.push(
          this.ensurePlacement(course, null).pipe(
            map((placement) => {
              const placementId = this.placementId(placement);
              if (!placementId) {
                return {
                  courseId: course.id,
                  ok: false,
                  placement,
                  error: 'placement_missing_id',
                } as BulkSyncResult;
              }
              this.upsertLocalPlacement(placement);
              return {
                courseId: course.id,
                ok: true,
                placement,
              } as BulkSyncResult;
            }),
            catchError((error) => of({
              courseId: course.id,
              ok: false,
              error,
            } as BulkSyncResult)),
          ),
        );
      }
    }
    if (calls.length === 0) {
      this.toastr.warning(
        this.t.instant('pages.tutors.placements.lms.bulkSyncNeedsPlacement'),
        '',
      );
      return;
    }
    this.bulkSyncing = true;
    this.openDistributeDialog('sync');
    this.runBatched(calls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          const prepared = results.filter((result) => (
            result.ok && result.placement
          ));
          const preparationFailedCount = results.filter((result) => !result.ok).length;

          for (const result of results) {
            const row = rowsByCourseId.get(result.courseId);
            if (!row) continue;
            if (result.ok && result.placement) {
              result.placement.course_sync_enabled = true;
              row.placement = result.placement;
              row.edit.courseSyncEnabled = true;
              row.initial.courseSyncEnabled = true;
            }
          }

          if (prepared.length === 0) {
            this.bulkSyncing = false;
            this.clearBulkProgress();
            for (const course of courses) {
              const row = rowsByCourseId.get(course.id);
              if (row) {
                row.savingSync = false;
              }
            }
            const firstError = results.find((result) => !result.ok)?.error;
            this.completeDistributeDialog(0, results.length);
            this.toastr.danger(
              this.resolveCourseSyncError(
                firstError,
                'pages.tutors.placements.lms.bulkSyncFailedToast',
              ),
              '',
            );
            this.load(false);
            return;
          }

          const placementIds = prepared
            .map((result) => this.placementId(result.placement as LmsPlacement))
            .filter((id): id is string => !!id);
          this.lms.enableCourseSyncBulk(placementIds)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (bulk) => {
                const queuedCount = bulk.queued_count || placementIds.length;
                this.bulkProgress = {
                  done: 0,
                  total: queuedCount,
                };
                this.setBulkProgressStatus(bulk.status || 'QUEUED');
                for (const result of prepared) {
                  if (!result.placement) continue;
                  const row = rowsByCourseId.get(result.courseId);
                  if (!row) continue;
                  result.placement.course_sync_enabled = true;
                  row.placement = result.placement;
                  row.edit.courseSyncEnabled = true;
                  row.initial.courseSyncEnabled = true;
                }
                if (bulk.job_id) {
                  // Ab hier arbeitet der Server allein weiter. Abfrage und
                  // Verteil-Modal gehen an den portalweiten Tracker — so
                  // kann der Nutzer „Weiterarbeiten" wählen und bekommt das
                  // Ergebnis-Modal trotzdem, egal auf welcher Seite er ist.
                  this.courseSyncTracker.track(
                    { jobId: bulk.job_id, preparationFailedCount },
                    this.distributeDialogRef,
                  );
                  this.distributeDialogRef = null;
                  this.watchBulkSync(
                    bulk.job_id,
                    rowsByCourseId,
                    courses,
                    preparationFailedCount,
                  );
                } else {
                  this.finishBulkSync(
                    rowsByCourseId,
                    courses,
                    queuedCount,
                    preparationFailedCount,
                    0,
                  );
                }
              },
              error: (err) => {
                this.bulkSyncing = false;
                this.clearBulkProgress();
                for (const course of courses) {
                  const row = rowsByCourseId.get(course.id);
                  if (row) {
                    row.savingSync = false;
                  }
                }
                this.completeDistributeDialog(0, courses.length);
                this.toastr.danger(this.resolveCourseSyncError(err), '');
              },
            });
        },
        error: (err) => {
          this.bulkSyncing = false;
          this.clearBulkProgress();
          for (const course of courses) {
            const row = rowsByCourseId.get(course.id);
            if (row) {
              row.savingSync = false;
            }
          }
          this.completeDistributeDialog(0, courses.length);
          this.toastr.danger(this.resolveBackendError(err, 'toasts.error'), '');
        },
      });
  }

  /** Hört beim `CourseSyncJobTrackerService` mit, solange die Karte lebt:
   *  Fortschrittsbalken, Zeilenstatus und am Ende Toast + Neuladen. Die
   *  Statusabfrage selbst und das Ergebnis-Modal gehören dem Tracker und
   *  laufen weiter, wenn der Nutzer die Seite verlässt. */
  private watchBulkSync(
    jobId: string,
    rowsByCourseId: Map<number, CourseRow>,
    courses: MoodleCourse[],
    preparationFailedCount: number,
  ): void {
    this.courseSyncTracker.updates(jobId)
      .pipe(
        takeWhile((update) => !update.finished, true),
        takeUntil(this.destroy$),
      )
      .subscribe((update) => {
        const job = update.status;
        if (job) {
          const done = (job.completed_courses || 0) + (job.failed_courses || 0);
          const total = job.total_courses || this.bulkProgress?.total || courses.length;
          this.bulkProgress = {
            done: Math.min(done, total),
            total,
          };
          this.setBulkProgressStatus(job.status);
        }
        if (!update.finished) return;
        this.finishBulkSync(
          rowsByCourseId,
          courses,
          job?.completed_courses || 0,
          preparationFailedCount,
          job?.failed_courses || 0,
          update.result?.reason,
        );
      });
  }

  private finishBulkSync(
    rowsByCourseId: Map<number, CourseRow>,
    courses: MoodleCourse[],
    successCount: number,
    preparationFailedCount: number,
    syncFailedCount: number,
    failureReason?: string,
  ): void {
    this.bulkSyncing = false;
    this.clearBulkProgress();
    for (const course of courses) {
      const row = rowsByCourseId.get(course.id);
      if (row) {
        row.savingSync = false;
      }
    }

    const failedCount = preparationFailedCount + syncFailedCount;
    this.completeDistributeDialog(successCount, failedCount);
    if (successCount === 0) {
      this.bulkRunDidacticsAfterSync = false;
      this.toastr.danger(
        failureReason || this.t.instant('pages.tutors.placements.lms.bulkSyncFailedToast'),
        '',
      );
      this.load(false);
      return;
    }

    // Second half of a combined run: the courses are queued, so the parse
    // now has content to work from. Confirmation was given up front.
    if (this.bulkRunDidacticsAfterSync) {
      this.bulkRunDidacticsAfterSync = false;
      this.applyBulkEnableStructureParse();
    }

    if (failedCount > 0) {
      this.toastr.warning(
        this.t.instant('pages.tutors.placements.lms.bulkSyncPartialToast', {
          success: successCount,
          failed: failedCount,
        }),
        '',
      );
    } else {
      this.toastr.success(
        this.t.instant('pages.tutors.placements.lms.bulkSyncToast', {
          count: successCount,
        }),
        '',
      );
    }
    this.clearSelection();
    this.organisationService.getOrganisation().pipe(takeUntil(this.destroy$)).subscribe();
    this.load(false);
  }

  bulkUnsyncSelected(): void {
    if (this.bulkBusy || this.selectedCount === 0) return;
    if (this.autoCourseSyncCoversAll) {
      this.toastr.info(
        this.t.instant('pages.tutors.placements.lms.siteAutoSyncBulkTooltip'),
        '',
      );
      return;
    }
    const courses = this.selectedCourses();
    const rowsByCourseId = new Map(this.rows.map((row) => [row.course.id, row]));
    const syncEnabled = courses
      .map((course) => ({
        course,
        placement: this.placementForCourse(course.id),
      }))
      .filter((entry): entry is { course: MoodleCourse; placement: LmsPlacement } => (
        !!entry.placement?.course_sync_enabled
      ));

    if (syncEnabled.length === 0) {
      this.toastr.warning(
        this.t.instant('pages.tutors.placements.lms.bulkUnsyncNeedsActiveSync'),
        '',
      );
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, {
        context: {
          title: 'pages.tutors.placements.lms.bulkUnsyncDialog.title',
          message: this.t.instant('pages.tutors.placements.lms.bulkUnsyncDialog.message', {
            count: syncEnabled.length,
          }),
          confirmLabel: 'pages.tutors.placements.lms.bulk.unsync',
          confirmStatus: 'primary',
        },
        hasBackdrop: true,
      })
      .onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        const calls: Observable<BulkUnsyncResult>[] = [];
        for (const { course, placement } of syncEnabled) {
          const row = rowsByCourseId.get(course.id);
          if (row) {
            row.savingSync = true;
          }
          const placementId = this.placementId(placement);
          if (!placementId) {
            calls.push(of({
              courseId: course.id,
              ok: false,
              error: 'placement_missing_id',
            }));
            continue;
          }
          calls.push(
            this.lms.disableCourseSync(placementId).pipe(
              map(() => ({
                courseId: course.id,
                ok: true,
              } as BulkUnsyncResult)),
              catchError((error) => of({
                courseId: course.id,
                ok: false,
                error,
              } as BulkUnsyncResult)),
            ),
          );
        }

        this.bulkUnsyncing = true;
        this.runBatched(calls)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (results) => {
              this.bulkUnsyncing = false;
              this.clearBulkProgress();
              const successCount = results.filter((result) => result.ok).length;
              const failedCount = results.length - successCount;
              for (const result of results) {
                const row = rowsByCourseId.get(result.courseId);
                if (!row) continue;
                row.savingSync = false;
                if (result.ok && row.placement) {
                  row.placement.course_sync_enabled = false;
                  row.edit.courseSyncEnabled = false;
                  row.initial.courseSyncEnabled = false;
                }
              }

              if (successCount === 0) {
                const firstError = results.find((result) => !result.ok)?.error;
                this.toastr.danger(
                  this.resolveCourseSyncError(
                    firstError,
                    'pages.tutors.placements.lms.bulkUnsyncFailedToast',
                  ),
                  '',
                );
                this.load(false);
                return;
              }

              if (failedCount > 0) {
                this.toastr.warning(
                  this.t.instant('pages.tutors.placements.lms.bulkUnsyncPartialToast', {
                    success: successCount,
                    failed: failedCount,
                  }),
                  '',
                );
              } else {
                this.toastr.success(
                  this.t.instant('pages.tutors.placements.lms.bulkUnsyncToast', {
                    count: successCount,
                  }),
                  '',
                );
              }
              this.clearSelection();
              this.load(false);
            },
            error: (err) => {
              this.bulkUnsyncing = false;
              this.clearBulkProgress();
              for (const { course } of syncEnabled) {
                const row = rowsByCourseId.get(course.id);
                if (row) {
                  row.savingSync = false;
                }
              }
              this.toastr.danger(this.resolveCourseSyncError(err), '');
            },
          });
      });
  }

  bulkRemoveSelected(): void {
    if (this.bulkBusy || this.selectedCount === 0) return;
    const courses = this.selectedCourses();
    const rowsByCourseId = new Map(this.rows.map((row) => [row.course.id, row]));
    const removable = courses
      .map((course) => ({
        course,
        placements: this.agentPlacementsForCourse(course.id),
      }))
      .filter((entry): entry is { course: MoodleCourse; placements: LmsPlacement[] } => entry.placements.length > 0);

    if (removable.length === 0) {
      this.toastr.warning(
        this.t.instant('pages.tutors.placements.lms.bulkRemoveNeedsPlacement'),
        '',
      );
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, {
        context: {
          title: 'pages.tutors.placements.lms.bulkRemoveDialog.title',
          message: this.t.instant('pages.tutors.placements.lms.bulkRemoveDialog.message', {
            count: removable.length,
          }),
          confirmLabel: 'pages.tutors.placements.lms.bulk.remove',
          confirmStatus: 'primary',
        },
        hasBackdrop: true,
      })
      .onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        const calls: Observable<BulkRemoveResult>[] = [];
        for (const { course, placements } of removable) {
          const row = rowsByCourseId.get(course.id);
          if (row) {
            row.saving = true;
            row.savingSync = false;
          }
          for (const placement of placements) {
            const placementId = this.placementId(placement);
            if (!placementId) {
              calls.push(of({
                courseId: course.id,
                ok: false,
                error: 'placement_missing_id',
              }));
              continue;
            }
            calls.push(
              this.lms.deletePlacement(placementId).pipe(
                map(() => ({
                  courseId: course.id,
                  ok: true,
                } as BulkRemoveResult)),
                catchError((error) => of({
                  courseId: course.id,
                  ok: false,
                  error,
                } as BulkRemoveResult)),
              ),
            );
          }
        }

        this.bulkRemoving = true;
        this.runBatched(calls)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (results) => {
              this.bulkRemoving = false;
              this.clearBulkProgress();
              const successCount = results.filter((result) => result.ok).length;
              const failedCount = results.length - successCount;
              for (const result of results) {
                const row = rowsByCourseId.get(result.courseId);
                if (row) {
                  row.saving = false;
                }
              }

              if (successCount === 0) {
                const firstError = results.find((result) => !result.ok)?.error;
                this.toastr.danger(
                  this.resolveBackendError(
                    firstError,
                    'pages.tutors.placements.lms.bulkRemoveFailedToast',
                  ),
                  '',
                );
                this.load(false);
                return;
              }

              if (failedCount > 0) {
                this.toastr.warning(
                  this.t.instant('pages.tutors.placements.lms.bulkRemovePartialToast', {
                    success: successCount,
                    failed: failedCount,
                  }),
                  '',
                );
              } else {
                this.toastr.success(
                  this.t.instant('pages.tutors.placements.lms.bulkRemoveToast', {
                    count: successCount,
                  }),
                  '',
                );
              }
              this.clearSelection();
              this.load(false);
            },
            error: (err) => {
              this.bulkRemoving = false;
              this.clearBulkProgress();
              for (const { course } of removable) {
                const row = rowsByCourseId.get(course.id);
                if (row) {
                  row.saving = false;
                }
              }
              this.toastr.danger(this.resolveBackendError(err, 'toasts.error'), '');
            },
          });
      });
  }

  private clampCoursePage(): void {
    const maxPage = Math.max(Math.ceil(this.courseTotal / this.coursePageSize) - 1, 0);
    this.coursePage = Math.max(0, Math.min(this.coursePage, maxPage));
  }

  refresh(): void {
    this.clearReadinessRetry();
    this.readinessAttempts = 0;
    this.refreshing = true;
    this.load(true);
  }

  /** Opens the synced-content overview (same course-structure dialog as
   *  the knowledge base) for a course whose sync is active. Resolves the
   *  local course id from (registration, moodle course) first. */
  /** Open the parsed Kurs-Didaktik view (chapters / objectives / learning
   *  path) — separate from "Synchronisierte Inhalte" (raw synced content). */
  openCourseStructure(row: CourseRow): void {
    const canonicalId = row.course.uuid;
    if (!canonicalId) return;
    this.dialog.open(ParsedCourseStructureDialogComponent, {
      context: {
        course: {
          id: canonicalId,
          fullname: row.course.fullname,
          shortname: (row.course as any).shortname,
        },
      },
      hasBackdrop: true,
    });
  }

  openSyncedContent(row: CourseRow): void {
    this.moodleService
      .lookupSyncedCourse(this.site.id, row.course.id, row.course.uuid || undefined)
      .subscribe({
        next: (res) => {
          this.dialog.open(CourseStructureDialogComponent, {
            context: {
              courseId: res.course_id,
              courseName: res.course_name || row.course.fullname,
            },
          });
        },
        error: () => {
          // 404 no_synced_course — sync enabled but first run not done yet
          this.toastr.info(
            this.t.instant('pages.tutors.placements.lms.rowActions.noSyncedContentToast'),
            this.t.instant('pages.tutors.placements.lms.rowActions.syncedContent')
          );
        },
      });
  }

  /** „Moodle-Kurs aktualisieren" im Zeilenmenü: alles, was wir von dem Kurs
   *  halten, frisch aus Moodle.
   *
   *  Bis 15.10.2026 frischte der Eintrag nur Struktur, Teilnehmende und Noten
   *  auf — die Kursinhalte nicht, und während er lief, verschwand lediglich
   *  der Menüpunkt. Wer „aktualisieren" klickte, sah nichts passieren und
   *  bekam die Inhalte doch nicht neu. Jetzt läuft derselbe Inhalts-Sync wie
   *  beim Kurs-Sync-Schalter (sofern der für die Zeile an ist), mit derselben
   *  Fortschrittsanzeige in der Zeile; die Metadaten laufen nebenher. Scheitert
   *  einer der beiden Teile, bleibt der andere gültig. */
  refreshCourse(row: CourseRow): void {
    const courseUuid = (row.course as any).uuid;
    if (!courseUuid || row.refreshingCourse || row.savingSync) return;
    row.refreshingCourse = true;

    const placement = row.placement || this.placementForCourse(row.course.id);
    const placementId = (placement?.moodle_placement_uuid || placement?.id) as string | undefined;
    const syncContent = !!placementId && this.effectiveCourseSyncEnabled(row);

    const metadata$ = this.dozentService.refreshMoodleCourse(courseUuid).pipe(
      // 200 with `errors`: some parts came back, others did not.
      map((result: any) => {
        const parts = Object.keys(result?.errors || {});
        return parts.length ? { partialParts: parts } : null;
      }),
      catchError((err) => of({ metadataError: err })),
    );
    const content$: Observable<unknown> = syncContent
      ? this.lms.enableCourseSync(placementId as string).pipe(
          catchError((err) => of({ contentError: err })),
        )
      : of(null);
    if (syncContent) {
      // Same badge as the toggle: the status cell reads `savingSync`.
      row.savingSync = true;
    }

    forkJoin([metadata$, content$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([metadata, content]: [any, any]) => {
        row.refreshingCourse = false;
        if (content?.contentError) {
          row.savingSync = false;
          this.toastr.danger(this.resolveCourseSyncError(content.contentError), '');
        } else if (syncContent && content?.job_id) {
          // Keeps `savingSync` until the queue reports the course done,
          // then toasts the outcome itself.
          this.pollSingleCourseSyncStatus(content.job_id, row);
        } else {
          row.savingSync = false;
        }
        if (metadata?.partialParts) {
          const parts = (metadata.partialParts as string[])
            .map((part) => this.t.instant(`pages.tutors.placements.lms.rowActions.refreshParts.${part}`))
            .join(', ');
          this.toastr.warning(
            this.t.instant('pages.tutors.placements.lms.rowActions.refreshCoursePartial', { parts }),
            '',
          );
        } else if (metadata?.metadataError) {
          const detail = metadata.metadataError?.error?.detail;
          this.toastr.warning(
            typeof detail === 'string' && detail
              ? detail
              : this.t.instant('pages.tutors.placements.lms.rowActions.refreshCourseError'),
            '',
          );
        } else if (!syncContent) {
          this.toastr.success(
            this.t.instant('pages.tutors.placements.lms.rowActions.refreshCourseSuccess'),
            '',
          );
        }
        this.load(false);
        this.refreshJobStatus();
      });
  }

  openAgentSettings(row: CourseRow): void {
    const botId = row.edit.botId || row.placement?.bot_id;
    if (!botId) return;
    this.router.navigate(['/tutors', botId, 'ai-settings']);
  }

  isPreviewActive(row: CourseRow): boolean {
    return this.chatPreviewService.isChatActive$.value
      && this.activePreviewCourseId === Number(row.course.id);
  }

  previewPlacementContext(row: CourseRow, event?: MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (this.isPreviewActive(row)) {
      this.chatPreviewService.closeChat();
      this.activePreviewCourseId = null;
      return;
    }

    const placement = this.agentPlacementForCourse(row.course.id);
    const botId = row.edit.botId || placement?.bot_id || null;
    if (!botId) {
      this.toastr.warning(
        this.t.instant('pages.tutors.placements.lms.rowActions.previewNoAgent'),
        '',
      );
      return;
    }

    const placementUuid = placement?.moodle_placement_uuid || placement?.id || null;
    this.chatPreviewService.connect(undefined, botId, {
      placementUuid,
      placement_uuid: placementUuid,
      siteIdentifier: this.site.moodle_site_identifier,
      site_identifier: this.site.moodle_site_identifier,
      courseId: row.course.id,
      context: {
        courseId: row.course.id,
      },
    });
    this.activePreviewCourseId = Number(row.course.id);
  }

  tutorName(botId?: string | null): string {
    if (!botId) return this.t.instant('pages.tutors.placements.lms.noAgent');
    const tutor = this.tutorById(botId);
    if (!tutor) return botId.slice(0, 8);
    const visualName = this.tutorVisualName(tutor);
    return visualName ? `${tutor.name} (${visualName})` : tutor.name;
  }

  dozentBotName(botId?: string | null): string {
    if (!botId) return '';
    const bot = this.dozentBotById(botId);
    return bot ? bot.name : botId.slice(0, 8);
  }

  openCreate(course: MoodleCourse, placement?: LmsPlacement): void {
    const ref = this.dialog.open(PlacementCreateDialogComponent, {
      context: {
        site: this.site,
        course,
        placement,
        tutors: this.tutors,
      },
      hasBackdrop: true,
    });
    ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((created) => {
      if (created) {
        this.toastr.success(
          this.t.instant('pages.tutors.placements.lms.createdToast'),
          '',
        );
        this.load();
      }
    });
  }

  openEdit(row: CourseRow): void {
    const placement = this.agentPlacementForCourse(row.course.id);
    if (!placement) return;
    const ref = this.dialog.open(PlacementEditDialogComponent, {
      context: {
        placement,
        course: row.course,
        tutors: this.tutors,
      },
      hasBackdrop: true,
    });
    ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((changed) => {
      if (changed) {
        this.toastr.success(
          this.t.instant('pages.tutors.placements.lms.updatedToast'),
          '',
        );
        this.load();
      }
    });
  }

  onDelete(row: CourseRow): void {
    const placements = this.agentPlacementsForCourse(row.course.id);
    if (placements.length === 0) return;
    // Replace native confirm() with our themed Nebular dialog so the
    // delete prompt matches the rest of the portal UX (translatable
    // copy, glass-morphism styling, branded buttons).
    this.dialog
      .open(ConfirmDialogComponent, {
        context: {
          title: 'pages.tutors.placements.lms.deleteDialog.title',
          message: 'pages.tutors.placements.lms.deleteDialog.message',
        },
        hasBackdrop: true,
      })
      .onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        const calls = placements
          .map((placement) => this.placementId(placement))
          .filter((placementId): placementId is string => !!placementId)
          .map((placementId) => this.lms.deletePlacement(placementId));
        if (calls.length === 0) return;
        row.saving = true;
        forkJoin(calls)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              row.saving = false;
              this.toastr.success(
                this.t.instant('pages.tutors.placements.lms.deletedToast'),
                '',
              );
              this.load();
            },
            error: (err) => {
              row.saving = false;
              this.toastr.danger(
                this.resolveBackendError(err, 'toasts.error'),
                '',
              );
            },
          });
      });
  }

  trackRow(_: number, r: CourseRow): number {
    return r.course.id;
  }
}
